import { useEffect, useRef } from "react";
import { io, Socket } from "socket.io-client";

const BOARD_EVENTS = [
  "note:created",
  "note:updated",
  "note:deleted",
  "note:moved",
  "action-items:changed",
  "phase:changed",
  "timer:updated",
  "participant:joined",
  "retro:activated",
  "retro:completed",
] as const;

/**
 * Subscribes to live board updates for `publicId` and calls `onUpdate` whenever
 * anything changes.
 *
 * `onUpdate` is held in a ref rather than listed as an effect dependency: it is
 * typically an inline callback, and depending on it tore the socket down and
 * rebuilt it on every render.
 */
export function useSocket(publicId: string, onUpdate?: () => void) {
  const socketRef = useRef<Socket | null>(null);
  const onUpdateRef = useRef(onUpdate);

  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  useEffect(() => {
    if (!publicId) return;

    const socket = io(window.location.origin, {
      path: "/socket.io",
      withCredentials: true,
    });

    socketRef.current = socket;

    const notify = () => onUpdateRef.current?.();

    socket.on("connect", () => {
      socket.emit("join-retro", { publicId });
    });

    BOARD_EVENTS.forEach((event) => socket.on(event, notify));

    socket.on("error", (error: unknown) => {
      console.error("Socket error:", error);
    });

    return () => {
      socket.emit("leave-retro", { publicId });
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [publicId]);

  return socketRef.current;
}
